
import React from 'react';
import { motion } from 'framer-motion';
import { Button } from '../components/ui/button';

const FrozenGoldPage = () => {
  const snowflakeVariants = {
    initial: { y: -20, opacity: 0 },
    animate: { y: 20, opacity: [0, 1, 0] },
  };

  return (
    <div className="min-h-screen bg-black">
      {/* Hero Section */}
      <section className="relative h-screen">
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-gradient-to-b from-blue-900/20 via-black to-black" />
          
          {/* Animated Snowflakes */}
          {Array.from({ length: 20 }).map((_, i) => (
            <motion.div
              key={i}
              className="absolute text-white text-opacity-30 text-4xl"
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
              }}
              variants={snowflakeVariants}
              initial="initial"
              animate="animate"
              transition={{
                duration: Math.random() * 2 + 2,
                repeat: Infinity,
                repeatType: "reverse",
                delay: Math.random() * 2,
              }}
            >
              ❄
            </motion.div>
          ))}
        </div>

        <div className="relative h-full flex items-center justify-center text-center px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1 }}
            className="max-w-4xl"
          >
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2, duration: 0.8 }}
              className="text-6xl md:text-7xl font-bold text-white mb-6"
            >
              Frozen Gold
            </motion.h1>
            
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.4, duration: 0.8 }}
              className="relative aspect-video max-w-3xl mx-auto mb-8 bg-white/10 backdrop-blur-sm rounded-lg overflow-hidden"
            >
              <motion.div 
                className="absolute inset-0 flex items-center justify-center"
                animate={{ 
                  backgroundColor: ["rgba(255,255,255,0.1)", "rgba(255,255,255,0.2)"],
                }}
                transition={{
                  duration: 2,
                  repeat: Infinity,
                  repeatType: "reverse",
                }}
              >
                <motion.span 
                  className="text-3xl font-bold text-white"
                  animate={{ 
                    scale: [1, 1.05, 1],
                    opacity: [1, 0.8, 1],
                  }}
                  transition={{
                    duration: 2,
                    repeat: Infinity,
                    repeatType: "reverse",
                  }}
                >
                  Coming Soon
                </motion.span>
              </motion.div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.6, duration: 0.8 }}
              className="space-y-6 max-w-2xl mx-auto"
            >
              <p className="text-xl text-blue-200">
                A journey that began as a high school project at Mohawk Mountain, 
                transforming into a powerful story about winter sports conservation 
                and the urgent need to protect our mountain environments.
              </p>

              <div className="grid md:grid-cols-2 gap-6 mt-12">
                <motion.div
                  className="bg-white/10 backdrop-blur-sm rounded-lg p-6"
                  whileHover={{ scale: 1.02 }}
                  transition={{ type: "spring", stiffness: 300 }}
                >
                  <h3 className="text-xl font-bold text-white mb-3">The Story</h3>
                  <p className="text-blue-200">
                    Through the lens of passionate winter sports enthusiasts, 
                    researchers, and industry leaders, we explore the challenges 
                    facing our beloved winter environments.
                  </p>
                </motion.div>

                <motion.div
                  className="bg-white/10 backdrop-blur-sm rounded-lg p-6"
                  whileHover={{ scale: 1.02 }}
                  transition={{ type: "spring", stiffness: 300 }}
                >
                  <h3 className="text-xl font-bold text-white mb-3">The Mission</h3>
                  <p className="text-blue-200">
                    More than a film, it's a call to action to preserve winter sports 
                    and mountain ecosystems for future generations.
                  </p>
                </motion.div>
              </div>

              <motion.div 
                className="pt-8"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.8 }}
              >
                <Button
                  size="lg"
                  className="bg-blue-600 hover:bg-blue-700 text-white px-8"
                  disabled
                >
                  Film Coming Soon
                </Button>
              </motion.div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Featured Voices Section */}
      <section className="py-24">
        <div className="max-w-6xl mx-auto px-4">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-3xl font-bold text-white text-center mb-16"
          >
            Featured Perspectives
          </motion.h2>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                title: "Industry Leaders",
                description: "Insights from those shaping the future of winter sports",
                icon: "🏔️"
              },
              {
                title: "Climate Scientists",
                description: "Research-backed analysis of environmental changes",
                icon: "🔬"
              },
              {
                title: "Local Communities",
                description: "Stories from those who call the mountains home",
                icon: "🏡"
              }
            ].map((item, index) => (
              <motion.div
                key={item.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.2 }}
                className="bg-white/10 backdrop-blur-sm rounded-lg p-6 text-center"
                whileHover={{ y: -10 }}
              >
                <motion.div 
                  className="text-4xl mb-4"
                  animate={{ 
                    scale: [1, 1.2, 1],
                    rotate: [0, 10, 0]
                  }}
                  transition={{
                    duration: 2,
                    repeat: Infinity,
                    repeatType: "reverse",
                    delay: index * 0.2
                  }}
                >
                  {item.icon}
                </motion.div>
                <h3 className="text-xl font-bold text-white mb-2">{item.title}</h3>
                <p className="text-blue-200">{item.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Newsletter Section */}
      <section className="py-24 bg-blue-900/20">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="space-y-6"
          >
            <h2 className="text-3xl font-bold text-white">
              Stay Updated
            </h2>
            <p className="text-xl text-blue-200">
              Be the first to know when Frozen Gold is released.
            </p>
            <div className="flex gap-4 max-w-md mx-auto">
              <input
                type="email"
                placeholder="Enter your email"
                className="flex-1 px-4 py-2 bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/50"
              />
              <Button 
                className="bg-blue-600 hover:bg-blue-700 text-white"
              >
                Notify Me
              </Button>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default FrozenGoldPage;
